import type { RouteObject } from 'react-router-dom';

import MainLayout from '@/app/layouts/MainLayout';
import { AuthGuard } from '@/app/router/guards';
import { lazyRoute } from '@/app/router/helpers';
import { ROUTES } from '@/shared/constants/app/routes.constant.ts';

/**
 * MainLayout이 적용되는 모든 라우트.
 *
 * 인증/Role 가드는 layout 내부에서 element wrapper로 중첩한다.
 * 새 페이지 추가 위치:
 *   - 마케팅/공개 페이지 → children 최상위
 *   - 인증 필요 페이지    → AuthGuard children
 *   - Role 제한 페이지     → RoleGuard children
 *   - 비인증만 접근가능 페이지 → GuestGuard children
 */
export const rootRoutes: RouteObject = {
  path: ROUTES.ROOT,
  Component: MainLayout,
  children: [
    // ── Public ─────────────────────────────────────────────
    {
      index: true,
      ...lazyRoute(() => import('@/pages/home/HomePage')),
    },
    {
      path: ROUTES.STORE,
      ...lazyRoute(() => import('@/pages/store/StorePage')),
    },
    {
      // 회사 소개 (공개)
      path: ROUTES.ABOUT,
      ...lazyRoute(() => import('@/pages/about/AboutPage')),
    },
    {
      // 플래그십 상품 상세. slug별 데이터는 페이지 내부에서 매핑.
      path: ROUTES.PRODUCT_DETAIL,
      ...lazyRoute(() => import('@/pages/products/ProductDetailPage')),
    },
    {
      // 다운로드 감사합니다 ( _ _)
      path: ROUTES.DOWNLOAD_THANKS,
      ...lazyRoute(() => import('@/pages/download-thanks/DownloadThanksPage')),
    },

    // 중도해지 정산금 셀프 결제 (운영자 발송 링크 — 비로그인 공개).
    // 인증은 URL 토큰이 대신하므로 AuthGuard 아래에 두면 안 된다.
    {
      path: ROUTES.SETTLEMENT_PAY,
      ...lazyRoute(() => import('@/pages/settlement/SettlementPayPage')),
    },

    // 결제 실패 리다이렉트 (Toss failUrl) — 서버 호출 없이 사유만 안내(공개)
    {
      path: ROUTES.PAYMENT_FAIL,
      ...lazyRoute(() => import('@/pages/payments/PaymentFailPage')),
    },
    // 자동결제 카드등록 실패 (Toss billing failUrl) — 사유만 안내(공개)
    {
      path: ROUTES.PAYMENT_BILLING_FAIL,
      ...lazyRoute(() => import('@/pages/payments/PaymentBillingFailPage')),
    },

    // ── Legal (약관/개인정보/환불정책 — 공개) ───────────────
    {
      path: ROUTES.LEGAL_TERMS,
      ...lazyRoute(() => import('@/pages/legal/TermsPage')),
    },
    {
      path: ROUTES.LEGAL_PRIVACY,
      ...lazyRoute(() => import('@/pages/legal/PrivacyPage')),
    },
    {
      path: ROUTES.LEGAL_REFUND,
      ...lazyRoute(() => import('@/pages/legal/RefundPage')),
    },

    // ── News (게시판) ──────────────────────────────────────
    {
      path: ROUTES.NEWS_NOTICES,
      ...lazyRoute(() => import('@/pages/news/NoticesPage')),
    },
    {
      path: ROUTES.NEWS_NOTICE_DETAIL,
      ...lazyRoute(() => import('@/pages/news/NoticeDetailPage')),
    },
    {
      path: ROUTES.NEWS_UPDATES,
      ...lazyRoute(() => import('@/pages/news/UpdatesPage')),
    },
    {
      path: ROUTES.NEWS_UPDATE_DETAIL,
      ...lazyRoute(() => import('@/pages/news/UpdateDetailPage')),
    },
    {
      path: ROUTES.NEWS_EVENTS,
      ...lazyRoute(() => import('@/pages/news/EventsPage')),
    },
    {
      path: ROUTES.NEWS_EVENT_DETAIL,
      ...lazyRoute(() => import('@/pages/news/EventDetailPage')),
    },
    {
      path: ROUTES.NEWS_ARCHIVES,
      ...lazyRoute(() => import('@/pages/news/ArchivesPage')),
    },
    {
      path: ROUTES.NEWS_ARCHIVE_DETAIL,
      ...lazyRoute(() => import('@/pages/news/ArchiveDetailPage')),
    },

    // ── Support (지원) — FAQ 공개 열람 (아코디언) ──────────
    {
      path: ROUTES.SUPPORT_FAQS,
      ...lazyRoute(() => import('@/pages/support/FaqsPage')),
    },
    // 협업(파트너십) 문의 — 비로그인도 작성·제출 가능(공개 라우트)
    {
      path: ROUTES.SUPPORT_COLLABORATION,
      ...lazyRoute(() => import('@/pages/support/CollaborationPage')),
    },

    // ── Auth required ──────────────────────────────────────
    {
      element: <AuthGuard />,
      children: [
        // 결제 성공 리다이렉트 (Toss successUrl) — confirm 에 인증 쿠키 필요.
        // 로딩 중엔 AuthGuard 가 스피너만 노출하므로 쿼리파라미터 유실 없음.
        {
          path: ROUTES.PAYMENT_SUCCESS,
          ...lazyRoute(() => import('@/pages/payments/PaymentSuccessPage')),
        },
        // 자동결제 카드등록 성공 → 구독 완성(billingKey→구독→최초청구). 인증 쿠키 필요.
        {
          path: ROUTES.PAYMENT_BILLING_SUCCESS,
          ...lazyRoute(
            () => import('@/pages/payments/PaymentBillingSuccessPage'),
          ),
        },

        // 게시판 작성/수정 — 로그인 필수.
        // 세부 권한(Role/소유권)은 각 View 내부 useBoardPermissions 가 forbidden 처리.
        // (write 가 ':id' 보다 먼저 선언돼 정적 세그먼트 우선 매칭)
        {
          path: ROUTES.NEWS_NOTICE_WRITE,
          ...lazyRoute(() => import('@/pages/news/NoticeWritePage')),
        },
        {
          path: ROUTES.NEWS_NOTICE_EDIT,
          ...lazyRoute(() => import('@/pages/news/NoticeEditPage')),
        },
        {
          path: ROUTES.NEWS_UPDATE_WRITE,
          ...lazyRoute(() => import('@/pages/news/UpdateWritePage')),
        },
        {
          path: ROUTES.NEWS_UPDATE_EDIT,
          ...lazyRoute(() => import('@/pages/news/UpdateEditPage')),
        },
        {
          path: ROUTES.NEWS_EVENT_WRITE,
          ...lazyRoute(() => import('@/pages/news/EventWritePage')),
        },
        {
          path: ROUTES.NEWS_EVENT_EDIT,
          ...lazyRoute(() => import('@/pages/news/EventEditPage')),
        },
        {
          path: ROUTES.NEWS_ARCHIVE_WRITE,
          ...lazyRoute(() => import('@/pages/news/ArchiveWritePage')),
        },
        {
          path: ROUTES.NEWS_ARCHIVE_EDIT,
          ...lazyRoute(() => import('@/pages/news/ArchiveEditPage')),
        },

        // ── Support: FAQ 작성/수정 (운영자 권한은 View 내부에서 검증) ──
        {
          path: ROUTES.SUPPORT_FAQ_WRITE,
          ...lazyRoute(() => import('@/pages/support/FaqWritePage')),
        },
        {
          path: ROUTES.SUPPORT_FAQ_EDIT,
          ...lazyRoute(() => import('@/pages/support/FaqEditPage')),
        },

        // ── Support: 1:1 문의 (전 경로 로그인 필수) ──
        // write 가 ':id' 보다 먼저 선언돼 정적 세그먼트 우선 매칭
        {
          path: ROUTES.SUPPORT_INQUIRIES,
          ...lazyRoute(() => import('@/pages/support/InquiriesPage')),
        },
        {
          path: ROUTES.SUPPORT_INQUIRY_WRITE,
          ...lazyRoute(() => import('@/pages/support/InquiryWritePage')),
        },
        {
          path: ROUTES.SUPPORT_INQUIRY_EDIT,
          ...lazyRoute(() => import('@/pages/support/InquiryEditPage')),
        },
        {
          path: ROUTES.SUPPORT_INQUIRY_DETAIL,
          ...lazyRoute(() => import('@/pages/support/InquiryDetailPage')),
        },

        // 마이페이지 (회원정보/라이선스/비밀번호/결제내역)
        {
          path: ROUTES.MYPAGE,
          ...lazyRoute(() => import('@/pages/mypage/MyPage')),
        },

        // 쿠폰 등록 (라이선스 발급)
        {
          path: ROUTES.COUPON,
          ...lazyRoute(() => import('@/pages/coupon/CouponPage')),
        },

        // 인증 유저 공통
        // { path: ROUTES.DASHBOARD, ...lazyRoute(() => import('@/pages/dashboard/DashboardPage')) },

        // Role 제한
        // {
        //   element: <RoleGuard allowedRoles={['SUPER_ADMIN']} />,
        //   children: [
        //     { path: ROUTES.SETTINGS, ...lazyRoute(() => import('@/pages/setting/SettingsPage')) },
        //   ],
        // },
      ],
    },
  ],
};
